import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import LoadingState from '../shared/LoadingState';
import { groupClientsByIdentity, reservationBelongsToClient } from '../../utils/clientIdentity';
import { 
  FileText, 
  Download, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  Users, 
  Package,
  Filter,
  BarChart3,
  Receipt,
  ClipboardList
} from 'lucide-react';
import './Reports.css';

const statusLabels = {
  pending: 'Pendente', confirmed: 'Confirmada', completed: 'Concluída', cancelled: 'Cancelada',
  no_show: 'Não compareceu', new: 'Novo', preparing: 'Em preparo', ready: 'Pronto', delivered: 'Finalizado'
};

const paymentLabels = { unpaid: 'Não pago', partial: 'Parcial', paid: 'Pago' };

const toExportDate = (value, includeTime = false) => {
  if (!value) return '-';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', includeTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' });
};

const money = value => Number(value) || 0;
const column = (header, key, type = 'text') => ({ header, key, type });

const buildExportModel = (report, data = {}) => {
  const summary = [];
  const sections = [];
  const addSummary = (label, value, type = 'text') => summary.push({ label, value, type });
  const addSection = (title, columns, rows) => sections.push({ title, columns, rows: rows || [] });

  if (report === 'sales') {
    addSummary('Receita recebida', money(data.summary?.totalPaid), 'currency');
    addSummary('Recebido em reservas', money(data.summary?.reservationPaid), 'currency');
    addSummary('Recebido no PDV', money(data.summary?.posPaid), 'currency');
    addSummary('A receber', money(data.summary?.totalPending), 'currency');
    addSummary('Reservas', data.summary?.totalReservations || 0, 'number');
    addSummary('Caixas do PDV', data.summary?.totalCashSessions || 0, 'number');
    addSummary('Ticket médio', money(data.summary?.averageTicket), 'currency');
    addSection('Receitas do PDV por caixa', [column('Data', 'date'), column('Ponto de venda', 'establishment'), column('Operador', 'operator'), column('Abertura', 'opening', 'currency'), column('Fechamento', 'closing', 'currency'), column('Vendas', 'sales', 'number'), column('Total pago', 'paid', 'currency'), column('Status', 'status')],
      (data.posCashSummaries || []).map(item => ({ date: toExportDate(item.date, true), establishment: item.establishmentName || '-', operator: item.operatorOpen || '-', opening: money(item.openingAmount), closing: item.closingAmount == null ? '-' : money(item.closingAmount), sales: item.salesCount || 0, paid: money(item.paidTotal), status: item.status === 'open' ? 'Aberto' : 'Fechado' })));
    addSection('Vendas por serviço', [column('Serviço', 'service'), column('Reservas', 'count', 'number'), column('Receita', 'revenue', 'currency')],
      (data.salesByService || []).map(item => ({ service: item.serviceName || '-', count: item.count || 0, revenue: money(item.revenue) })));
    addSection('Reservas', [column('Data', 'date'), column('Cliente', 'client'), column('Serviço', 'service'), column('Valor', 'amount', 'currency'), column('Pago', 'paid', 'currency'), column('Status', 'status'), column('Pagamento', 'payment')],
      (data.reservations || []).map(item => ({ date: toExportDate(item.date), client: item.clientName || '-', service: item.serviceName || '-', amount: money(item.totalAmount), paid: money(item.paidAmount), status: statusLabels[item.status] || item.status || '-', payment: paymentLabels[item.paymentStatus] || item.paymentStatus || '-' })));
  } else if (report === 'commissions') {
    addSummary('Comissões totais', money(data.summary?.totalCommissions), 'currency');
    addSummary('Vendas totais', money(data.summary?.totalSales), 'currency');
    addSummary('Taxa média', money(data.summary?.averageCommissionRate), 'percent');
    addSummary('Colaboradores', data.summary?.collaboratorsCount || 0, 'number');
    addSection('Comissões por colaborador', [column('Colaborador', 'name'), column('E-mail', 'email'), column('Reservas', 'count', 'number'), column('Vendas', 'sales', 'currency'), column('Comissões', 'commission', 'currency'), column('Taxa efetiva', 'rate', 'percent')],
      (data.commissionsByCollaborator || []).map(item => ({ name: item.name || '-', email: item.email || '-', count: item.reservationsCount || 0, sales: money(item.totalSales), commission: money(item.totalCommission), rate: item.totalSales > 0 ? (money(item.totalCommission) / money(item.totalSales)) * 100 : 0 })));
  } else if (report === 'reservations') {
    addSummary('Total', data.summary?.total || 0, 'number');
    addSummary('Pendentes', data.summary?.byStatus?.pending || 0, 'number');
    addSummary('Confirmadas', data.summary?.byStatus?.confirmed || 0, 'number');
    addSummary('Concluídas', data.summary?.byStatus?.completed || 0, 'number');
    addSummary('Canceladas', data.summary?.byStatus?.cancelled || 0, 'number');
    addSummary('Não compareceram', data.summary?.byStatus?.noShow || 0, 'number');
    addSection('Reservas', [column('Data', 'date'), column('Cliente', 'client'), column('CPF', 'cpf'), column('Serviço', 'service'), column('Participantes', 'participants', 'number'), column('Valor', 'amount', 'currency'), column('Status', 'status'), column('Pagamento', 'payment')],
      (data.reservations || []).map(item => ({ date: toExportDate(item.date), client: item.clientName || '-', cpf: item.clientCpf || '-', service: item.serviceName || '-', participants: item.participants || 0, amount: money(item.totalAmount), status: statusLabels[item.status] || item.status || '-', payment: paymentLabels[item.paymentStatus] || item.paymentStatus || '-' })));
  } else if (report === 'clients') {
    addSummary('Total de clientes', data.summary?.totalClients || 0, 'number');
    addSummary('Clientes ativos', data.summary?.activeClients || 0, 'number');
    addSummary('Valor total', money(data.summary?.totalSpent), 'currency');
    addSummary('Média por cliente', money(data.summary?.averageSpent), 'currency');
    addSection('Clientes', [column('Nome', 'name'), column('CPF', 'cpf'), column('Telefone', 'phone'), column('E-mail', 'email'), column('Reservas', 'reservations', 'number'), column('Valor total', 'spent', 'currency'), column('Última reserva', 'last')],
      (data.clients || []).map(item => ({ name: item.name || '-', cpf: item.cpf || '-', phone: item.phone || '-', email: item.email || '-', reservations: item.totalReservations || 0, spent: money(item.totalSpent), last: toExportDate(item.lastReservation?.date) })));
  } else if (report === 'services') {
    addSummary('Total de serviços', data.summary?.totalServices || 0, 'number');
    addSummary('Total de reservas', data.summary?.totalReservations || 0, 'number');
    addSummary('Receita total', money(data.summary?.totalRevenue), 'currency');
    addSummary('Média por serviço', money(data.summary?.averageRevenuePerService), 'currency');
    addSection('Serviços', [column('Serviço', 'name'), column('Tipo', 'type'), column('Capacidade', 'capacity', 'number'), column('Preço', 'price', 'currency'), column('Reservas', 'reservations', 'number'), column('Receita', 'revenue', 'currency'), column('Ticket médio', 'average', 'currency')],
      (data.services || []).map(item => ({ name: item.name || '-', type: item.isPousada ? 'Pousada' : item.type || '-', capacity: item.capacity || 0, price: money(item.price), reservations: item.totalReservations || 0, revenue: money(item.totalRevenue), average: money(item.averageTicket) })));
  } else if (report === 'orders') {
    addSummary('Pedidos no período', data.summary?.total || 0, 'number');
    addSummary('Em andamento', data.summary?.active || 0, 'number');
    addSummary('Finalizados', data.summary?.delivered || 0, 'number');
    addSummary('Cancelados', data.summary?.cancelled || 0, 'number');
    addSummary('Valor dos pedidos', money(data.summary?.totalAmount), 'currency');
    addSection('Pedidos', [column('Data', 'date'), column('Cliente / mesa', 'customer'), column('Ponto de venda', 'establishment'), column('Itens', 'items', 'number'), column('Valor', 'amount', 'currency'), column('Aberto por', 'createdBy'), column('Status', 'status')],
      (data.orders || []).map(item => ({ date: toExportDate(item.createdAt, true), customer: item.customerName || item.table || '-', establishment: item.establishmentName || '-', items: item.itemCount || (item.items || []).reduce((sum, product) => sum + (Number(product.quantity) || 0), 0), amount: money(item.totalAmount), createdBy: item.createdByName || item.createdByEmail || 'Link público', status: statusLabels[item.status] || item.status || '-' })));
  }

  return { summary, sections };
};

const Reports = () => {
  const { userData } = useAuth();
  
  const getCompanyId = useCallback(() => {
    return userData?.role === 'collaborator' 
      ? userData?.currentCompanyId 
      : userData?.companyId;
  }, [userData]);

  const [activeReport, setActiveReport] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Filtros
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: 'all',
    collaboratorEmail: userData?.role === 'collaborator' ? userData.email : 'all',
    serviceId: 'all',
    paymentStatus: 'all'
  });

  const [services, setServices] = useState([]);
  const [collaborators, setCollaborators] = useState([]);

  const loadServices = useCallback(async () => {
    try {
      const servicesRef = collection(db, `companies/${getCompanyId()}/services`);
      const snapshot = await getDocs(servicesRef);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServices(data);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
    }
  }, [getCompanyId]);

  const loadCollaborators = useCallback(async () => {
    try {
      const collabsRef = collection(db, `companies/${getCompanyId()}/collaborators`);
      const snapshot = await getDocs(collabsRef);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCollaborators(data);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  }, [getCompanyId]);

  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      switch (activeReport) {
        case 'sales':
          await generateSalesReport();
          break;
        case 'commissions':
          await generateCommissionsReport();
          break;
        case 'reservations':
          await generateReservationsReport();
          break;
        case 'clients':
          await generateClientsReport();
          break;
        case 'services':
          await generateServicesReport();
          break;
        case 'orders':
          await generateOrdersReport();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setLoading(false);
    }
  }, [activeReport, getCompanyId, filters, services]);

  const generateSalesReport = async () => {
    const reservationsRef = collection(db, `companies/${getCompanyId()}/reservations`);
    const posSalesRef = collection(db, `companies/${getCompanyId()}/posSales`);
    const cashSessionsRef = collection(db, `companies/${getCompanyId()}/cashSessions`);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);

    let reservationsQuery = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );

    const [snapshot, posSalesSnapshot, cashSessionsSnapshot] = await Promise.all([
      getDocs(reservationsQuery),
      getDocs(query(posSalesRef, where('status', '==', 'paid'))),
      getDocs(cashSessionsRef)
    ]);

    let reservations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const posSales = posSalesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const cashSessions = cashSessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Aplicar filtros adicionais
    if (filters.status !== 'all') {
      reservations = reservations.filter(r => r.status === filters.status);
    }
    if (filters.paymentStatus !== 'all') {
      reservations = reservations.filter(r => r.paymentStatus === filters.paymentStatus);
    }
    if (filters.collaboratorEmail !== 'all') {
      reservations = reservations.filter(r => r.createdByEmail === filters.collaboratorEmail);
    }
    if (filters.serviceId !== 'all') {
      reservations = reservations.filter(r => r.serviceId === filters.serviceId);
    }

    // Calcular métricas
    const totalRevenue = reservations.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    const reservationPaid = reservations.reduce((sum, r) => sum + (r.paidAmount || 0), 0);
    const totalPending = totalRevenue - reservationPaid;
    const totalReservations = reservations.length;
    const confirmedReservations = reservations.filter(r => r.status === 'confirmed' || r.status === 'completed').length;

    const getDateValue = (date) => {
      if (!date) return null;
      const parsed = date.toDate ? date.toDate() : new Date(date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const isInsidePeriod = (date) => {
      const parsed = getDateValue(date);
      return parsed && parsed >= startDate && parsed <= endDate;
    };

    let posCashSummaries = cashSessions
      .filter(session => isInsidePeriod(session.openedAt || session.createdAt))
      .map(session => {
        const sessionSales = posSales.filter(sale => sale.cashSessionId === session.id);
        return {
          ...session,
          date: session.openedAt || session.createdAt,
          paidTotal: sessionSales.reduce((sum, sale) => sum + (Number(sale.totalAmount) || 0), 0),
          salesCount: sessionSales.length,
          openingAmount: Number(session.openingAmount) || 0,
          closingAmount: session.closingAmount === undefined || session.closingAmount === null ? null : Number(session.closingAmount) || 0,
          operatorOpen: session.openedByEmail || '-',
          operatorClose: session.closedByEmail || '-'
        };
      });

    if (filters.collaboratorEmail !== 'all') {
      posCashSummaries = posCashSummaries.filter(cash => (
        cash.operatorOpen === filters.collaboratorEmail || cash.operatorClose === filters.collaboratorEmail
      ));
    }

    posCashSummaries = posCashSummaries.sort((a, b) => {
      const aTime = getDateValue(a.date)?.getTime() || 0;
      const bTime = getDateValue(b.date)?.getTime() || 0;
      return bTime - aTime;
    });

    const posPaid = posCashSummaries.reduce((sum, cash) => sum + (cash.paidTotal || 0), 0);
    const totalPaid = reservationPaid + posPaid;

    // Agrupar por dia
    const salesByDay = {};
    reservations.forEach(r => {
      const date = r.date.toDate ? r.date.toDate() : new Date(r.date);
      const dateKey = date.toISOString().split('T')[0];
      if (!salesByDay[dateKey]) {
        salesByDay[dateKey] = { date: dateKey, revenue: 0, count: 0 };
      }
      salesByDay[dateKey].revenue += r.totalAmount || 0;
      salesByDay[dateKey].count += 1;
    });

    // Agrupar por serviço
    const salesByService = {};
    reservations.forEach(r => {
      if (!salesByService[r.serviceId]) {
        salesByService[r.serviceId] = {
          serviceId: r.serviceId,
          serviceName: r.serviceName,
          revenue: 0,
          count: 0
        };
      }
      salesByService[r.serviceId].revenue += r.totalAmount || 0;
      salesByService[r.serviceId].count += 1;
    });

    setReportData({
      summary: {
        totalRevenue,
        totalPaid,
        reservationPaid,
        posPaid,
        totalPending,
        totalReservations,
        totalCashSessions: posCashSummaries.length,
        confirmedReservations,
        averageTicket: totalReservations > 0 ? totalRevenue / totalReservations : 0
      },
      salesByDay: Object.values(salesByDay).sort((a, b) => a.date.localeCompare(b.date)),
      salesByService: Object.values(salesByService).sort((a, b) => b.revenue - a.revenue),
      posCashSummaries,
      reservations: reservations
    });
  };

  const generateCommissionsReport = async () => {
    const reservationsRef = collection(db, `companies/${getCompanyId()}/reservations`);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);

    let q = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    let reservations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filtrar por colaborador se necessário
    if (filters.collaboratorEmail !== 'all') {
      reservations = reservations.filter(r => r.createdByEmail === filters.collaboratorEmail);
    }

    // Apenas reservas confirmadas ou concluídas
    reservations = reservations.filter(r => r.status === 'confirmed' || r.status === 'completed');

    // Buscar comissões dos serviços
    const servicesMap = {};
    for (const service of services) {
      servicesMap[service.id] = service;
    }

    // Calcular comissões por colaborador
    const commissionsByCollaborator = {};
    for (const reservation of reservations) {
      const email = reservation.createdByEmail || 'sem_email';
      const service = servicesMap[reservation.serviceId];
      const commissionRate = service?.commission || 0;
      const commissionAmount = (reservation.totalAmount * commissionRate) / 100;

      if (!commissionsByCollaborator[email]) {
        commissionsByCollaborator[email] = {
          email,
          name: reservation.createdBy || email,
          totalSales: 0,
          totalCommission: 0,
          reservationsCount: 0,
          reservations: []
        };
      }

      commissionsByCollaborator[email].totalSales += reservation.totalAmount;
      commissionsByCollaborator[email].totalCommission += commissionAmount;
      commissionsByCollaborator[email].reservationsCount += 1;
      commissionsByCollaborator[email].reservations.push({
        ...reservation,
        commissionRate,
        commissionAmount
      });
    }

    const totalCommissions = Object.values(commissionsByCollaborator).reduce(
      (sum, c) => sum + c.totalCommission, 0
    );
    const totalSales = Object.values(commissionsByCollaborator).reduce(
      (sum, c) => sum + c.totalSales, 0
    );

    setReportData({
      summary: {
        totalCommissions,
        totalSales,
        averageCommissionRate: totalSales > 0 ? (totalCommissions / totalSales) * 100 : 0,
        collaboratorsCount: Object.keys(commissionsByCollaborator).length
      },
      commissionsByCollaborator: Object.values(commissionsByCollaborator).sort(
        (a, b) => b.totalCommission - a.totalCommission
      )
    });
  };

  const generateReservationsReport = async () => {
    const reservationsRef = collection(db, `companies/${getCompanyId()}/reservations`);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);

    let q = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    let reservations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Aplicar filtros
    if (filters.status !== 'all') {
      reservations = reservations.filter(r => r.status === filters.status);
    }
    if (filters.collaboratorEmail !== 'all') {
      reservations = reservations.filter(r => r.createdByEmail === filters.collaboratorEmail);
    }
    if (filters.serviceId !== 'all') {
      reservations = reservations.filter(r => r.serviceId === filters.serviceId);
    }

    // Métricas por status
    const byStatus = {
      pending: reservations.filter(r => r.status === 'pending').length,
      confirmed: reservations.filter(r => r.status === 'confirmed').length,
      completed: reservations.filter(r => r.status === 'completed').length,
      cancelled: reservations.filter(r => r.status === 'cancelled').length,
      noShow: reservations.filter(r => r.status === 'no_show').length
    };

    // Métricas por pagamento
    const byPayment = {
      unpaid: reservations.filter(r => r.paymentStatus === 'unpaid').length,
      partial: reservations.filter(r => r.paymentStatus === 'partial').length,
      paid: reservations.filter(r => r.paymentStatus === 'paid').length
    };

    setReportData({
      summary: {
        total: reservations.length,
        byStatus,
        byPayment
      },
      reservations
    });
  };

  const generateClientsReport = async () => {
    const clientsRef = collection(db, `companies/${getCompanyId()}/clients`);
    const snapshot = await getDocs(clientsRef);
    let clients = groupClientsByIdentity(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    // Filtrar por colaborador se necessário
    if (filters.collaboratorEmail !== 'all') {
      clients = clients.filter(c => c.createdBy === filters.collaboratorEmail);
    }

    // Buscar reservas dos clientes
    const reservationsRef = collection(db, `companies/${getCompanyId()}/reservations`);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);

    const reservationsQuery = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const reservationsSnapshot = await getDocs(reservationsQuery);
    const reservations = reservationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Calcular dados por cliente
    const clientsData = clients.map(client => {
      const clientReservations = reservations.filter(reservation => reservationBelongsToClient(reservation, client));
      const totalSpent = clientReservations.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
      const totalReservations = clientReservations.length;

      return {
        ...client,
        totalSpent,
        totalReservations,
        lastReservation: clientReservations.length > 0 
          ? clientReservations.sort((a, b) => {
              const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date);
              const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date);
              return dateB - dateA;
            })[0]
          : null
      };
    });

    const totalClients = clientsData.length;
    const totalSpent = clientsData.reduce((sum, c) => sum + c.totalSpent, 0);
    const activeClients = clientsData.filter(c => c.totalReservations > 0).length;

    setReportData({
      summary: {
        totalClients,
        activeClients,
        totalSpent,
        averageSpent: totalClients > 0 ? totalSpent / totalClients : 0
      },
      clients: clientsData.sort((a, b) => b.totalSpent - a.totalSpent)
    });
  };

  const generateServicesReport = async () => {
    const servicesRef = collection(db, `companies/${getCompanyId()}/services`);
    const servicesSnapshot = await getDocs(servicesRef);
    const servicesList = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const reservationsRef = collection(db, `companies/${getCompanyId()}/reservations`);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);

    const reservationsQuery = query(
      reservationsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const reservationsSnapshot = await getDocs(reservationsQuery);
    let reservations = reservationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Filtrar por status
    if (filters.status !== 'all') {
      reservations = reservations.filter(r => r.status === filters.status);
    }

    // Calcular dados por serviço
    const servicesData = servicesList.map(service => {
      const serviceReservations = reservations.filter(r => r.serviceId === service.id);
      const totalRevenue = serviceReservations.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
      const totalReservations = serviceReservations.length;

      return {
        ...service,
        totalRevenue,
        totalReservations,
        averageTicket: totalReservations > 0 ? totalRevenue / totalReservations : 0
      };
    });

    const totalRevenue = servicesData.reduce((sum, s) => sum + s.totalRevenue, 0);
    const totalReservations = servicesData.reduce((sum, s) => sum + s.totalReservations, 0);

    setReportData({
      summary: {
        totalServices: servicesData.length,
        totalRevenue,
        totalReservations,
        averageRevenuePerService: servicesData.length > 0 ? totalRevenue / servicesData.length : 0
      },
      services: servicesData.sort((a, b) => b.totalRevenue - a.totalRevenue)
    });
  };

  const generateOrdersReport = async () => {
    const ordersRef = collection(db, `companies/${getCompanyId()}/orders`);
    const startDate = new Date(`${filters.startDate}T00:00:00`);
    const endDate = new Date(`${filters.endDate}T23:59:59.999`);
    const ordersSnapshot = await getDocs(query(
      ordersRef,
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      orderBy('createdAt', 'desc')
    ));
    let orders = ordersSnapshot.docs.map(item => ({ id: item.id, ...item.data() }));

    if (filters.status !== 'all') {
      orders = orders.filter(order => order.status === filters.status);
    }
    if (filters.collaboratorEmail !== 'all') {
      orders = orders.filter(order => order.createdByEmail === filters.collaboratorEmail);
    }

    const activeOrders = orders.filter(order => ['new', 'preparing', 'ready'].includes(order.status));
    const deliveredOrders = orders.filter(order => order.status === 'delivered');
    const cancelledOrders = orders.filter(order => order.status === 'cancelled');
    const validOrders = orders.filter(order => order.status !== 'cancelled');
    const totalAmount = validOrders.reduce((sum, order) => sum + (Number(order.totalAmount) || 0), 0);

    setReportData({
      summary: {
        total: orders.length,
        active: activeOrders.length,
        ready: orders.filter(order => order.status === 'ready').length,
        delivered: deliveredOrders.length,
        cancelled: cancelledOrders.length,
        totalAmount
      },
      orders
    });
  };

  useEffect(() => {
    if (getCompanyId()) {
      loadServices();
      if (userData?.role !== 'collaborator') {
        loadCollaborators();
      }
    }
  }, [getCompanyId, loadServices, loadCollaborators, userData?.role]);

  useEffect(() => {
    if (getCompanyId()) {
      generateReport();
    }
  }, [getCompanyId, generateReport]);

  const getExportContext = () => {
    const report = reportTypes.find(item => item.id === activeReport);
    return {
      title: `Relatório de ${report?.label || 'dados'}`,
      period: `${filters.startDate.split('-').reverse().join('/')} a ${filters.endDate.split('-').reverse().join('/')}`,
      fileName: `relatorio-${activeReport}-${filters.startDate}-${filters.endDate}`,
      model: buildExportModel(activeReport, reportData)
    };
  };

  const formatExportValue = (value, type) => {
    if (type === 'currency') return formatCurrency(value);
    if (type === 'percent') return `${(Number(value) || 0).toFixed(2)}%`;
    return value ?? '-';
  };

  const exportToPDF = async () => {
    if (!reportData) return;
    const [{ jsPDF }, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    const autoTable = autoTableModule.default;
    const { title, period, fileName, model } = getExportContext();
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFontSize(16);
    pdf.setTextColor(15, 23, 42);
    pdf.text(title, 14, 16);
    pdf.setFontSize(9);
    pdf.setTextColor(71, 85, 105);
    pdf.text(`Período: ${period}`, 14, 22);
    pdf.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth - 14, 22, { align: 'right' });

    autoTable(pdf, {
      startY: 28,
      head: [['Indicador', 'Valor']],
      body: model.summary.map(item => [item.label, formatExportValue(item.value, item.type)]),
      theme: 'grid',
      tableWidth: 105,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [15, 118, 110], textColor: 255 }
    });

    let currentY = (pdf.lastAutoTable?.finalY || 28) + 10;
    model.sections.forEach(section => {
      if (currentY > 180) {
        pdf.addPage();
        currentY = 16;
      }
      pdf.setFontSize(11);
      pdf.setTextColor(15, 23, 42);
      pdf.text(section.title, 14, currentY);
      autoTable(pdf, {
        startY: currentY + 3,
        head: [section.columns.map(item => item.header)],
        body: section.rows.length
          ? section.rows.map(row => section.columns.map(item => formatExportValue(row[item.key], item.type)))
          : [[...section.columns.map((_, index) => index === 0 ? 'Nenhum registro encontrado' : '')]],
        theme: 'striped',
        margin: { left: 14, right: 14 },
        styles: { fontSize: 7, cellPadding: 1.8, overflow: 'linebreak' },
        headStyles: { fillColor: [30, 41, 59], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: hookData => {
          pdf.setFontSize(7);
          pdf.setTextColor(100);
          pdf.text(`Página ${pdf.internal.getNumberOfPages()}`, pageWidth - 14, pdf.internal.pageSize.getHeight() - 6, { align: 'right' });
          if (hookData.pageNumber > 1) currentY = 16;
        }
      });
      currentY = (pdf.lastAutoTable?.finalY || currentY) + 10;
    });

    pdf.save(`${fileName}.pdf`);
  };

  const exportToExcel = async () => {
    if (!reportData) return;
    const XLSX = await import('xlsx');
    const { title, period, fileName, model } = getExportContext();
    const workbook = XLSX.utils.book_new();
    const summaryRows = [
      [title],
      [`Período: ${period}`],
      [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
      [],
      ['Indicador', 'Valor'],
      ...model.summary.map(item => [item.label, item.value])
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
    summarySheet['!cols'] = [{ wch: 28 }, { wch: 20 }];
    model.summary.forEach((item, index) => {
      const cell = summarySheet[`B${index + 6}`];
      if (!cell) return;
      if (item.type === 'currency') cell.z = 'R$ #,##0.00';
      if (item.type === 'percent') cell.z = '0.00"%"';
    });
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    model.sections.forEach((section, sectionIndex) => {
      const rows = section.rows.map(row => Object.fromEntries(section.columns.map(item => [item.header, row[item.key] ?? ''])));
      const sheet = XLSX.utils.json_to_sheet(rows, { header: section.columns.map(item => item.header) });
      sheet['!cols'] = section.columns.map(item => ({ wch: Math.min(36, Math.max(12, item.header.length + 3)) }));
      section.rows.forEach((_, rowIndex) => {
        section.columns.forEach((item, columnIndex) => {
          const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })];
          if (!cell) return;
          if (item.type === 'currency') cell.z = 'R$ #,##0.00';
          if (item.type === 'percent') cell.z = '0.00"%"';
        });
      });
      const sheetName = `${sectionIndex + 1}-${section.title}`
        .replace(/[\\/?*:]/g, '')
        .replaceAll('[', '')
        .replaceAll(']', '')
        .slice(0, 31);
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    });

    XLSX.writeFile(workbook, `${fileName}.xlsx`, { compression: true });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR');
  };

  const reportTypes = [
    { id: 'sales', label: 'Vendas', icon: DollarSign },
    { id: 'commissions', label: 'Comissões', icon: TrendingUp },
    { id: 'reservations', label: 'Reservas', icon: Calendar },
    { id: 'clients', label: 'Clientes', icon: Users },
    { id: 'services', label: 'Serviços', icon: Package },
    { id: 'orders', label: 'Pedidos', icon: ClipboardList }
  ];

  return (
    <div className="reports-container">
      <div className="reports-header">
        <div>
          <h1>Relatórios</h1>
          <p>Análise completa do desempenho do negócio</p>
        </div>
        <div className="reports-actions">
          <button className="btn-secondary" onClick={exportToExcel} disabled={!reportData || loading}>
            <Download size={18} />
            Excel
          </button>
          <button className="btn-primary" onClick={exportToPDF} disabled={!reportData || loading}>
            <Download size={18} />
            PDF
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="report-tabs">
        {reportTypes.map(type => {
          const Icon = type.icon;
          return (
            <button
              key={type.id}
              className={`report-tab ${activeReport === type.id ? 'active' : ''}`}
              onClick={() => {
                setActiveReport(type.id);
                setFilters(current => ({ ...current, status: 'all' }));
                setReportData(null);
              }}
            >
              <Icon size={20} />
              {type.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="reports-filters">
        <div className="filter-group">
          <label>Data Inicial</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Data Final</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>

        {(activeReport === 'sales' || activeReport === 'reservations' || activeReport === 'orders') && (
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">Todos</option>
              {activeReport === 'orders' ? (
                <>
                  <option value="new">Novo</option>
                  <option value="preparing">Em preparo</option>
                  <option value="ready">Pronto</option>
                  <option value="delivered">Finalizado</option>
                </>
              ) : (
                <>
                  <option value="pending">Pendente</option>
                  <option value="confirmed">Confirmada</option>
                  <option value="completed">Concluída</option>
                </>
              )}
              <option value="cancelled">Cancelada</option>
              <option value="no_show">Não compareceu</option>
            </select>
          </div>
        )}

        {activeReport === 'sales' && (
          <div className="filter-group">
            <label>Pagamento</label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
            >
              <option value="all">Todos</option>
              <option value="unpaid">Não Pago</option>
              <option value="partial">Parcial</option>
              <option value="paid">Pago</option>
            </select>
          </div>
        )}

        {userData?.role !== 'collaborator' && (activeReport === 'sales' || activeReport === 'commissions' || activeReport === 'reservations' || activeReport === 'clients' || activeReport === 'orders') && (
          <div className="filter-group">
            <label>Colaborador</label>
            <select
              value={filters.collaboratorEmail}
              onChange={(e) => setFilters({ ...filters, collaboratorEmail: e.target.value })}
            >
              <option value="all">Todos</option>
              {collaborators.map(collab => (
                <option key={collab.id} value={collab.userEmail}>
                  {collab.userName}
                </option>
              ))}
            </select>
          </div>
        )}

        {(activeReport === 'sales' || activeReport === 'reservations') && (
          <div className="filter-group">
            <label>Serviço</label>
            <select
              value={filters.serviceId}
              onChange={(e) => setFilters({ ...filters, serviceId: e.target.value })}
            >
              <option value="all">Todos</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button className="btn-filter" onClick={generateReport}>
          <Filter size={18} />
          Aplicar Filtros
        </button>
      </div>

      {/* Report Content */}
      {loading ? (
        <LoadingState
          variant="block"
          title="Gerando relatorio"
          subtitle="Cruzando filtros e resultados"
          bars={2}
        />
      ) : reportData ? (
        <div className="reports-content">
          {activeReport === 'sales' && <SalesReport data={reportData} formatCurrency={formatCurrency} formatDate={formatDate} />}
          {activeReport === 'commissions' && <CommissionsReport data={reportData} formatCurrency={formatCurrency} />}
          {activeReport === 'reservations' && <ReservationsReport data={reportData} formatDate={formatDate} formatCurrency={formatCurrency} />}
          {activeReport === 'clients' && <ClientsReport data={reportData} formatCurrency={formatCurrency} formatDate={formatDate} />}
          {activeReport === 'services' && <ServicesReport data={reportData} formatCurrency={formatCurrency} />}
          {activeReport === 'orders' && <OrdersReport data={reportData} formatCurrency={formatCurrency} />}
        </div>
      ) : (
        <div className="reports-empty">
          <FileText size={64} color="#ccc" />
          <p>Selecione os filtros e clique em "Aplicar Filtros"</p>
        </div>
      )}
    </div>
  );
};

// Sales Report Component
const SalesReport = ({ data, formatCurrency, formatDate }) => {
  const salesByService = data?.salesByService || [];
  const reservations = data?.reservations || [];
  const posCashSummaries = data?.posCashSummaries || [];
  const totalRevenue = data?.summary?.totalRevenue || 1;
  
  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Receita Recebida</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalPaid || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Recebido em Reservas</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.reservationPaid || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#ecfeff', color: '#0891b2' }}>
            <Receipt size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Recebido no PDV</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.posPaid || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">A Receber</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalPending || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Caixas do PDV</p>
            <h2 className="summary-value">{data?.summary?.totalCashSessions || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fce7f3', color: '#ec4899' }}>
            <TrendingUp size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Ticket Médio</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.averageTicket || 0)}</h2>
          </div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Receitas do PDV por Caixa</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Dia</th>
              <th>Caixa</th>
              <th>Operador</th>
              <th>Abertura</th>
              <th>Fechamento</th>
              <th>Vendas pagas</th>
              <th>Total pago</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {posCashSummaries.length > 0 ? (
              posCashSummaries.map(cash => (
                <tr key={cash.id}>
                  <td data-label="Dia">{formatDate(cash.date)}</td>
                  <td data-label="Caixa">{cash.establishmentName || 'PDV'}</td>
                  <td data-label="Operador">
                    <div className="cash-operator">
                      <span>Abriu: {cash.operatorOpen}</span>
                      <span>Fechou: {cash.operatorClose}</span>
                    </div>
                  </td>
                  <td data-label="Abertura">{formatCurrency(cash.openingAmount)}</td>
                  <td data-label="Fechamento">{cash.closingAmount === null ? '-' : formatCurrency(cash.closingAmount)}</td>
                  <td data-label="Vendas pagas">{cash.salesCount}</td>
                  <td data-label="Total pago">{formatCurrency(cash.paidTotal)}</td>
                  <td data-label="Status">
                    <span className={`payment-badge payment-${cash.status === 'closed' ? 'paid' : 'partial'}`}>
                      {cash.status === 'closed' ? 'Fechado' : 'Aberto'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhum caixa do PDV encontrado no período
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sales by Service */}
      <div className="report-table-section">
        <h3>Vendas por Serviço</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Serviço</th>
              <th>Reservas</th>
              <th>Faturamento</th>
              <th>Participação</th>
            </tr>
          </thead>
          <tbody>
            {salesByService.length > 0 ? (
              salesByService.map((item, index) => (
                <tr key={index}>
                  <td data-label="Serviço">{item.serviceName}</td>
                  <td data-label="Reservas">{item.count}</td>
                  <td data-label="Faturamento">{formatCurrency(item.revenue)}</td>
                  <td data-label="Participação">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0}%`,
                          background: '#667eea'
                        }}
                      />
                    </div>
                    {totalRevenue > 0 ? ((item.revenue / totalRevenue) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhuma venda registrada no período
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent Reservations */}
      <div className="report-table-section">
        <h3>Reservas Recentes</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Cliente</th>
              <th>Serviço</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Pagamento</th>
            </tr>
          </thead>
          <tbody>
            {reservations.length > 0 ? (
              reservations.slice(0, 10).map((reservation) => (
                <tr key={reservation.id}>
                  <td data-label="Data">{formatDate(reservation.date)}</td>
                  <td data-label="Cliente">{reservation.clientName}</td>
                  <td data-label="Serviço">{reservation.serviceName}</td>
                  <td data-label="Valor">{formatCurrency(reservation.totalAmount)}</td>
                  <td data-label="Status">
                    <span className={`status-badge status-${reservation.status}`}>
                      {reservation.status}
                    </span>
                  </td>
                  <td data-label="Pagamento">
                    <span className={`payment-badge payment-${reservation.paymentStatus}`}>
                      {reservation.paymentStatus}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhuma reserva encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Commissions Report Component
const CommissionsReport = ({ data, formatCurrency }) => {
  const averageRate = data?.summary?.averageCommissionRate || 0;
  
  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <TrendingUp size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Comissões Totais</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalCommissions || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Vendas Totais</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalSales || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <BarChart3 size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Taxa Média</p>
            <h2 className="summary-value">{averageRate.toFixed(2)}%</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <Users size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Colaboradores</p>
            <h2 className="summary-value">{data?.summary?.collaboratorsCount || 0}</h2>
          </div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Comissões por Colaborador</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Colaborador</th>
              <th>Reservas</th>
              <th>Vendas</th>
              <th>Comissões</th>
              <th>Taxa Efetiva</th>
            </tr>
          </thead>
          <tbody>
            {data?.commissionsByCollaborator && data.commissionsByCollaborator.length > 0 ? (
              data.commissionsByCollaborator.map((collab, index) => (
                <tr key={index}>
                  <td data-label="Colaborador">{collab.name}</td>
                  <td data-label="Reservas">{collab.reservationsCount}</td>
                  <td data-label="Vendas">{formatCurrency(collab.totalSales)}</td>
                  <td data-label="Comissões">{formatCurrency(collab.totalCommission)}</td>
                  <td data-label="Taxa efetiva">{collab.totalSales > 0 ? ((collab.totalCommission / collab.totalSales) * 100).toFixed(2) : '0.00'}%</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhum dado de comissão disponível para o período selecionado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Reservations Report Component
const ReservationsReport = ({ data, formatDate, formatCurrency }) => {
  const byStatus = data?.summary?.byStatus || {};
  const reservations = data?.reservations || [];
  
  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total</p>
            <h2 className="summary-value">{data?.summary?.total || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Pendentes</p>
            <h2 className="summary-value">{byStatus.pending || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Confirmadas</p>
            <h2 className="summary-value">{byStatus.confirmed || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Concluídas</p>
            <h2 className="summary-value">{byStatus.completed || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Canceladas</p>
            <h2 className="summary-value">{byStatus.cancelled || 0}</h2>
          </div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Todas as Reservas</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Cliente</th>
              <th>Serviço</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Pagamento</th>
            </tr>
          </thead>
          <tbody>
            {reservations.length > 0 ? (
              reservations.map((reservation) => (
                <tr key={reservation.id}>
                  <td data-label="Data">{formatDate(reservation.date)}</td>
                  <td data-label="Cliente">{reservation.clientName}</td>
                  <td data-label="Serviço">{reservation.serviceName}</td>
                  <td data-label="Valor">{formatCurrency(reservation.totalAmount)}</td>
                  <td data-label="Status">
                    <span className={`status-badge status-${reservation.status}`}>
                      {reservation.status}
                    </span>
                  </td>
                  <td data-label="Pagamento">
                    <span className={`payment-badge payment-${reservation.paymentStatus}`}>
                      {reservation.paymentStatus}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhuma reserva encontrada para o período selecionado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrdersReport = ({ data, formatCurrency }) => {
  const orders = data?.orders || [];
  const statusLabels = {
    new: 'Novo',
    preparing: 'Em preparo',
    ready: 'Pronto',
    delivered: 'Finalizado',
    cancelled: 'Cancelado'
  };
  const formatDateTime = value => {
    if (!value) return '-';
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#4f46e5' }}><ClipboardList size={24} /></div>
          <div className="summary-content"><p className="summary-label">Pedidos no período</p><h2 className="summary-value">{data?.summary?.total || 0}</h2></div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#d97706' }}><Receipt size={24} /></div>
          <div className="summary-content"><p className="summary-label">Em andamento</p><h2 className="summary-value">{data?.summary?.active || 0}</h2></div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><ClipboardList size={24} /></div>
          <div className="summary-content"><p className="summary-label">Finalizados</p><h2 className="summary-value">{data?.summary?.delivered || 0}</h2></div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><ClipboardList size={24} /></div>
          <div className="summary-content"><p className="summary-label">Cancelados</p><h2 className="summary-value">{data?.summary?.cancelled || 0}</h2></div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#ecfeff', color: '#0891b2' }}><DollarSign size={24} /></div>
          <div className="summary-content"><p className="summary-label">Valor dos pedidos</p><h2 className="summary-value">{formatCurrency(data?.summary?.totalAmount || 0)}</h2></div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Histórico completo de pedidos</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Cliente / mesa</th>
              <th>Ponto de venda</th>
              <th>Itens</th>
              <th>Valor</th>
              <th>Aberto por</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? orders.map(order => (
              <tr key={order.id}>
                <td data-label="Data">{formatDateTime(order.createdAt)}</td>
                <td data-label="Cliente / mesa">{order.customerName || order.table || '-'}</td>
                <td data-label="Ponto de venda">{order.establishmentName || '-'}</td>
                <td data-label="Itens">{order.itemCount || (order.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}</td>
                <td data-label="Valor">{formatCurrency(order.totalAmount)}</td>
                <td data-label="Aberto por">{order.createdByName || order.createdByEmail || 'Link público'}</td>
                <td data-label="Status"><span className={`status-badge order-status-${order.status}`}>{statusLabels[order.status] || order.status}</span></td>
              </tr>
            )) : (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhum pedido encontrado no período.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Clients Report Component
const ClientsReport = ({ data, formatCurrency, formatDate }) => {
  const clients = data?.clients || [];
  
  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <Users size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total de Clientes</p>
            <h2 className="summary-value">{data?.summary?.totalClients || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <Users size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Clientes Ativos</p>
            <h2 className="summary-value">{data?.summary?.activeClients || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Faturamento Total</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalSpent || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <TrendingUp size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Ticket Médio</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.averageSpent || 0)}</h2>
          </div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Clientes por Faturamento</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Email</th>
              <th>Telefone</th>
              <th>Reservas</th>
              <th>Total Gasto</th>
              <th>Última Reserva</th>
            </tr>
          </thead>
          <tbody>
            {clients.length > 0 ? (
              clients.map((client) => (
                <tr key={client.id}>
                  <td data-label="Cliente">{client.name}</td>
                  <td data-label="Email">{client.email || '-'}</td>
                  <td data-label="Telefone">{client.phone || '-'}</td>
                  <td data-label="Reservas">{client.totalReservations}</td>
                  <td data-label="Total gasto">{formatCurrency(client.totalSpent)}</td>
                  <td data-label="Última reserva">{client.lastReservation ? formatDate(client.lastReservation.date) : '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhum cliente encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Services Report Component
const ServicesReport = ({ data, formatCurrency }) => {
  const services = data?.services || [];
  const totalRevenue = data?.summary?.totalRevenue || 1;
  
  return (
    <div className="report-section">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#e0e7ff', color: '#6366f1' }}>
            <Package size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total de Serviços</p>
            <h2 className="summary-value">{data?.summary?.totalServices || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
            <DollarSign size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Faturamento Total</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.totalRevenue || 0)}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total de Reservas</p>
            <h2 className="summary-value">{data?.summary?.totalReservations || 0}</h2>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <TrendingUp size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Faturamento Médio</p>
            <h2 className="summary-value">{formatCurrency(data?.summary?.averageRevenuePerService || 0)}</h2>
          </div>
        </div>
      </div>

      <div className="report-table-section">
        <h3>Serviços por Desempenho</h3>
        <table className="report-table">
          <thead>
            <tr>
              <th>Serviço</th>
              <th>Tipo</th>
              <th>Reservas</th>
              <th>Faturamento</th>
              <th>Ticket Médio</th>
              <th>Desempenho</th>
            </tr>
          </thead>
          <tbody>
            {services.length > 0 ? (
              services.map((service) => (
                <tr key={service.id}>
                  <td data-label="Serviço">{service.name}</td>
                  <td data-label="Tipo">
                    <span className="service-type-badge">
                      {service.isPousada ? 'Pousada' : service.type}
                    </span>
                  </td>
                  <td data-label="Reservas">{service.totalReservations}</td>
                  <td data-label="Faturamento">{formatCurrency(service.totalRevenue)}</td>
                  <td data-label="Ticket médio">{formatCurrency(service.averageTicket)}</td>
                  <td data-label="Desempenho">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${totalRevenue > 0 ? (service.totalRevenue / totalRevenue) * 100 : 0}%`,
                          background: '#667eea'
                        }}
                      />
                    </div>
                    {totalRevenue > 0 ? ((service.totalRevenue / totalRevenue) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Nenhum serviço encontrado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Reports;
